import {
    convertDateArrayFromDate,
    convertDateFromDateArray,
} from '@starjack/utils';
import { MultiLangString, StarjackDate } from './general';
import { parse } from 'date-fns';
import { Environment } from '@/models/environment';

export const PC_DEFAULT_DATE_START = parse(
    '2000-01-01',
    'yyyy-MM-dd',
    new Date()
);
export const PC_DEFAULT_DATE_END = parse(
    '2100-01-01',
    'yyyy-MM-dd',
    new Date()
);

export type IntegrationConfiguration = {
    barcode: boolean;
    internalProduction: boolean;
    keycard: boolean;
    pickup: boolean;
    mobileFlow: boolean;
};

export const allEnvironments = [
    Environment.PORTAL,
    Environment.INTEGRATION,
    Environment.B2B,
    Environment.HOSPITALITY,
];

export enum ProductType {
    SEASON = 'SEASON',
    DAY = 'DAY',
}

export type ProductName = {
    de: string;
    en: string;
};

export type Product = {
    duration: number;
    name: ProductName;
    type: ProductType;
};

export type FilterTag = {
    id: string;
    nameDe: string;
    nameEn: string | null;
    sortOrder: number;
};

export type ProductTag = {
    id: string;
    name: string;
    sortOrder: number;
};

export type ProductConfiguration = {
    enabled: boolean;
    id: string;
    disableStartDateToday: boolean;
    // ISO local time "HH:mm:ss" (Europe/Vienna); null = no cutoff
    bookingCutoffTime: string | null;
    displayEndDate: boolean;
    startDateLabel?: StarjackDate;
    allowZeroPrice: boolean;
    skipFaceValidation: boolean;
    keycardCodes: string[];
    liftOperatorId: string;
    conditionLinkIds: string[];
    integrationConfiguration: IntegrationConfiguration;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    hospitalityConfiguration: IntegrationConfiguration;
    product?: Product;
    persons: {
        name: MultiLangString;
    }[];
    productId: string;
    infoTextDE: string;
    infoTextEN: string;
    pickupInfoTextDE: string;
    pickupInfoTextEN: string;
    barcodeInfoTextDE: string;
    barcodeInfoTextEN: string;
    filterTags: FilterTag[];
    sortKey: number;
    maxTicketsPerStartDay: number;
    contingentsThreshold: number;
    tags: ProductTag[];
    validityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    startDateValidityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    endDate?: StarjackDate;
    durationOverride: number;
    productTypeOverride: ProductType;
    birthdayRequired: boolean;
};

export type BackendProductConfiguration = {
    enabled: boolean;
    allowZeroPrice: boolean;
    skipFaceValidation: boolean;
    id: string;
    disableStartDateToday: boolean;
    // ISO local time "HH:mm:ss" (Europe/Vienna); null = no cutoff
    bookingCutoffTime: string | null;
    displayEndDate: boolean;
    startDateLabel?: number[];
    integrationConfiguration: IntegrationConfiguration;
    keycardCodes: string[];
    liftOperatorId: string;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    hospitalityConfiguration: IntegrationConfiguration;
    pickupInfoText: MultiLangString;
    barcodeInfoText: MultiLangString;
    product?: Product;
    persons: {
        name: MultiLangString;
    }[];
    productId: string;
    sortKey: number;
    maxTicketsPerStartDay: number;
    conditionLinkIds: string[];
    contingentsThreshold: number;
    tags: ProductTag[];
    infoText: MultiLangString;
    filterTags: FilterTag[];
    validityRange: {
        from: number[];
        to: number[];
    };
    startDateValidityRange: {
        from: number[];
        to: number[];
    };
    endDate?: number[];
    durationOverride: number;
    productTypeOverride: ProductType;
    birthdayRequired: boolean;
};

export type ProductConfigurationUpdatePayload = Omit<
    BackendProductConfiguration,
    'tags' | 'filterTags'
> & {
    tagIds: string[];
    filterTagIds: string[];
};

export const transformProductConfigurationFromResponse = (
    bPc: BackendProductConfiguration
): ProductConfiguration => {
    return {
        ...bPc,
        startDateLabel: convertDateFromDateArray(bPc.startDateLabel),
        pickupInfoTextDE: bPc.pickupInfoText?.de,
        pickupInfoTextEN: bPc.pickupInfoText?.en,
        barcodeInfoTextDE: bPc.barcodeInfoText?.de,
        barcodeInfoTextEN: bPc.barcodeInfoText?.en,
        infoTextDE: bPc.infoText?.de,
        infoTextEN: bPc.infoText?.en,
        validityRange: {
            from: convertDateFromDateArray(bPc.validityRange.from),
            to: convertDateFromDateArray(bPc.validityRange.to),
        },
        startDateValidityRange: {
            from: convertDateFromDateArray(bPc.startDateValidityRange.from),
            to: convertDateFromDateArray(bPc.startDateValidityRange.to),
        },
        endDate: convertDateFromDateArray(bPc.endDate),
    };
};

export const transformProductConfigurationToBackend = (
    p: ProductConfiguration
): ProductConfigurationUpdatePayload => {
    // PC_DEFAULT_DATE_START
    // PC_DEFAULT_DATE_END

    const configuration = {
        ...p,
        infoText: {
            de: p.infoTextDE,
            en: p.infoTextEN,
        },
        pickupInfoText: {
            de: p.pickupInfoTextDE,
            en: p.pickupInfoTextEN,
        },
        barcodeInfoText: {
            de: p.barcodeInfoTextDE,
            en: p.barcodeInfoTextEN,
        },
        tagIds: p.tags.map((t) => t.id),
        filterTagIds: p.filterTags.map((t) => t.id),
        productTypeOverride: p.productTypeOverride || null,
        validityRange: {
            from: convertDateArrayFromDate(p.validityRange.from),
            to: convertDateArrayFromDate(p.validityRange.to),
        },

        startDateValidityRange: {
            from: convertDateArrayFromDate(
                p.startDateValidityRange.from || PC_DEFAULT_DATE_START
            ),
            to: convertDateArrayFromDate(
                p.startDateValidityRange.to || PC_DEFAULT_DATE_END
            ),
        },
        endDate: p.endDate ? convertDateArrayFromDate(p.endDate) : undefined,
        startDateLabel: p.startDateLabel
            ? convertDateArrayFromDate(p.startDateLabel)
            : undefined,
    } as unknown as ProductConfigurationUpdatePayload;

    delete (configuration as unknown as ProductConfiguration).infoTextDE;
    delete (configuration as unknown as ProductConfiguration).infoTextEN;
    delete (configuration as unknown as ProductConfiguration).tags;
    delete (configuration as unknown as ProductConfiguration).filterTags;
    delete (configuration as unknown as ProductConfiguration).pickupInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).pickupInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).barcodeInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).barcodeInfoTextEN;

    return configuration;
};
